import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendSampleInstance,
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
	FrontendCompanyTestCategory,
	FrontendBatchType,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
} from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomDropdown,
	CustomRadioGroup,
	CustomTextInput,
	UserInfoTextInput,
} from "../../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import {
	FrontendContentPage,
	MicroTestMethod,
	Severity,
	SystemRole,
} from "node-library/dist/constants";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";

import { AnalystCustomGroup } from "node-library/dist/database";
import { getMicroTestMethodTranslationText } from "../../../helpers/sampleHelper";
import { GreenButton } from "../../../common-components/button";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import BatchGenerationInstanceContainer from "./components/batchGenerationInstanceContainer";
import CommonAutoCompleteDropdown from "../../../common-components/commonAutoCompleteDropdown";
import { getDefaultOtherTestBatchType } from "../../../helpers/testBatchHelper";
import {
	BatchAccessLevel,
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	user: FrontendUserInfo | undefined;
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	navigateToBatchDetailsPage: (
		batchId: string,
		fromPage: FrontendContentPage,
	) => void;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function BatchGenerationPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { user, company, setCompany } = props;

	const userHasFullBatchAccess: boolean =
		user?.role?.accessLevel.limitedBatchAccess === BatchAccessLevel.FULL_ACCESS;

	const userCanAssignOtherAnalysts: boolean =
		user &&
		[BatchAccessLevel.FULL_ACCESS, BatchAccessLevel.LIMITED_ACCESS].includes(
			user?.role?.accessLevel.limitedBatchAccess as BatchAccessLevel,
		)
			? true
			: false;
	const setUnsavedChanges = props.setUnsavedChanges;

	const [loading, setLoading] = useState<boolean>(false);

	const [analysts, setAnalysts] = useState<Array<FrontendUserInfo>>([]); //excluding self

	const permittedCategories: Array<FrontendCompanyTestCategory> =
		userHasFullBatchAccess
			? company.companyTestCategories
			: company.companyTestCategories.filter((category) => {
					let batchTests = [...(category.batchTypes || [])];

					if (!userHasFullBatchAccess) {
						batchTests = batchTests.filter((batchType) =>
							user?.analystRoleGroup?.permittedBatchIds.includes(
								batchType.batchTypeId,
							),
						);
					}

					// 'Other' test permission
					const hasOtherTestPermission =
						userHasFullBatchAccess ||
						user?.analystRoleGroup?.testCategoryOtherTestPermissions.includes(
							category.testCategoryId,
						);

					const alreadyIncluded = batchTests.some(
						(batch) => batch.batchTypeId === "other",
					);

					if (hasOtherTestPermission && !alreadyIncluded) {
						batchTests.push(
							getDefaultOtherTestBatchType(
								category.testCategoryId,
								translation,
							),
						);
					}

					return batchTests.length > 0;
				});

	const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
		permittedCategories && permittedCategories.length > 0
			? permittedCategories[0].testCategoryId
			: "",
	);

	const selectedCategory: FrontendCompanyTestCategory | undefined =
		permittedCategories.find((category) => {
			return category.testCategoryId == selectedCategoryId;
		});

	const otherTestBatch: FrontendBatchType = getDefaultOtherTestBatchType(
		selectedCategoryId,
		translation,
	);
	const availableBatchTests: Array<FrontendBatchType> = useMemo(() => {
		let batchTests = [...(selectedCategory?.batchTypes || [])];

		const hasOtherTestPermission =
			userHasFullBatchAccess ||
			user?.analystRoleGroup?.testCategoryOtherTestPermissions.includes(
				selectedCategoryId,
			);

		const alreadyIncluded = batchTests.some(
			(batch) => batch.batchTypeId === otherTestBatch.batchTypeId,
		);

		if (!userHasFullBatchAccess) {
			batchTests = batchTests.filter((batchType) =>
				user?.analystRoleGroup?.permittedBatchIds.includes(
					batchType.batchTypeId,
				),
			);
		}

		if (hasOtherTestPermission && !alreadyIncluded) {
			batchTests.push(otherTestBatch);
		}

		return batchTests;
	}, [
		selectedCategory,
		selectedCategoryId,
		user,
		userHasFullBatchAccess,
		otherTestBatch,
	]);

	const [selectedBatchTypeId, setSelectedBatchTypeId] = useState<string>(
		availableBatchTests.length > 0 ? availableBatchTests[0].batchTypeId : "",
	);

	const selectedBatchType: FrontendBatchType | undefined =
		availableBatchTests.find((batchType) => {
			return batchType.batchTypeId == selectedBatchTypeId;
		});

	const [assignedAnalysts, setAssignedAnalysts] = useState<
		Array<{ title: string; id: string }>
	>(
		userCanAssignOtherAnalysts
			? []
			: [
					{
						title: `${user?.firstName} ${user?.lastName}`,
						id: "self",
					},
				],
	);

	const [batchCodePrefix, setBatchCodePrefix] = useState<string>("");

	const [allInstances, setAllInstances] = useState<
		Array<FrontendSampleInstance>
	>([]);
	const [instancesLoaded, setInstancesLoaded] = useState<boolean>(false);

	//sample instances to include in batch
	const [instancesToBatch, setInstancesToBatch] = useState<
		Array<FrontendSampleInstance>
	>([]);
	const [requireDuplicateInstance, setRequireDuplicateInstance] =
		useState<boolean>(false);
	const [instanceToVerify, setInstanceToVerify] = useState<
		FrontendSampleInstance | undefined
	>();
	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	async function getAnalysts() {
		setLoading(true);
		try {
			const response = await sendGETRequest("/users/get-company-analysts", {});

			const analysts: Array<FrontendUserInfo> = response?.data?.analysts;
			setAnalysts(analysts);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	async function handlePopulateBatch() {
		setLoading(true);
		setInstancesLoaded(false);
		try {
			const response = await sendGETRequest(
				"/sample-test-batches/batch-generation-get-registered-instance-info",
				{
					selectedBatchTypeId: selectedBatchTypeId,
					selectedTestCategoryId: selectedCategoryId,
				},
			);

			let frontendUnbatchedInstances: Array<FrontendSampleInstance> =
				response?.data?.frontendUnbatchedInstances;
			frontendUnbatchedInstances = frontendUnbatchedInstances.sort((a, b) => {
				const dateA = new Date(a.expectedCompletionTime ?? 0).getTime();
				const dateB = new Date(b.expectedCompletionTime ?? 0).getTime();
				return dateA - dateB;
			});

			setAllInstances(frontendUnbatchedInstances);
			setInstancesToBatch([]);
			//fill with the first 20 samples
			// const first20 = frontendUnbatchedInstances.slice(0, 20);
			// setInstancesToBatch(first20);

			// Pick the first one to verify
			// if (first20.length > 0) {
			// 	setInstanceToVerify(first20[0]);
			// } else {
			// 	setInstanceToVerify(undefined);
			// }
			setInstanceToVerify(undefined);
			const specDirectories: Array<FrontendSampleSpecificationDirectory> =
				response?.data?.specDirectories;
			setSpecDirectories(specDirectories);
			setInstancesLoaded(true);
			openSnackbar(
				translation("InstancesPopulated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	async function handleBatchGeneration() {
		//vaildate the info first
		// let errorTitle: string = "";
		// let errorMsg: string = "";
		// if (instancesToBatch.length ==0) {
		// 	errorTitle=translation("NoInstanceSelectedError")
		// 	errorMsg = translation("NoInstanceSelectedErrorDescription")
		// }
		setLoading(true);
		try {
			const doesUserHaveAdminCreateAccess: boolean = props.user
				? hasPermission(
						props.user?.role?.permissions as PermissionMap,
						Permissions.batches.adminGenerate,
					)
				: false;

			let response;
			if (doesUserHaveAdminCreateAccess) {
				response = await sendPOSTRequest(
					"/sample-test-batches/admin-generate-batch",
					{
						instanceIdsToBatch: instancesToBatch.map((instance) => {
							return instance.sampleInstanceId;
						}),
						selectedBatchTypeId: selectedBatchTypeId,
						selectedTestCategoryId: selectedCategoryId,

						assignedAnalystIds: assignedAnalysts.map((analyst) => {
							return analyst.id;
						}),
						instanceIdToVerify: instanceToVerify?.sampleInstanceId,
						batchCodePrefix,
					},
				);
			} else {
				response = await sendPOSTRequest(
					"/sample-test-batches/analyst-generate-batch",
					{
						instanceIdsToBatch: instancesToBatch.map((instance) => {
							return instance.sampleInstanceId;
						}),
						selectedTestCategoryId: selectedCategoryId,

						selectedBatchTypeId: selectedBatchTypeId,
						instanceIdToVerify: instanceToVerify?.sampleInstanceId,
						batchCodePrefix,
					},
				);
			}

			const newBatchId: string = response?.data?.newBatchId;
			openSnackbar(
				translation("BatchCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			if (newBatchId) {
				//spoof to prevent coming back
				props.navigateToBatchDetailsPage(
					newBatchId,
					FrontendContentPage.BATCH_PAGE,
				);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (user) {
			if (userCanAssignOtherAnalysts) {
				getAnalysts();
			}
			setSelectedBatchTypeId(availableBatchTests[0].batchTypeId);
			setRequireDuplicateInstance(
				availableBatchTests[0].requireDuplicateInstance,
			);
		}
	}, [user]);

	const sortedBatchTestsOptions: Array<FrontendBatchType> =
		availableBatchTests.sort((a, b) => {
			const categoryCompare = a.testGroupNodeId.localeCompare(
				b.testGroupNodeId,
			);
			if (categoryCompare !== 0) return categoryCompare;

			// If testCategoryId is the same, sort by batchName
			return a.batchName.localeCompare(b.batchName);
		});

	const analystHavingPermission = analysts.filter(
		(analyst: FrontendUserInfo) => {
			return selectedBatchTypeId == "other"
				? analyst.analystRoleGroup?.testCategoryOtherTestPermissions.includes(
						selectedCategoryId,
					)
				: analyst.analystRoleGroup?.permittedBatchIds.includes(
						selectedBatchTypeId,
					);
		},
	);
	let analystOptions = analystHavingPermission.map(
		(analyst: FrontendUserInfo) => {
			return {
				title: `${analyst.firstName} ${analyst.lastName}`,
				id: analyst.userId,
			};
		},
	);
	// analystOptions.push({ title: `--${translation("None")}--`, value: "" });

	const categoryDropdownOptions: Array<{
		title: string;
		value: string;
		renderValue?: string;
	}> = permittedCategories.map((category) => ({
		title: category.testCategoryName,
		value: category.testCategoryId,
	}));

	const selectCategoryDropdown: React.ReactNode = (
		<CustomDropdown
			id="category-dropdown"
			label={translation("TestCategory")}
			options={categoryDropdownOptions}
			value={selectedCategoryId}
			setValue={(value) => {
				setInstancesLoaded(false);

				setSelectedCategoryId(value);
				const newCategory: FrontendCompanyTestCategory =
					permittedCategories.find((category) => {
						return category.testCategoryId == value;
					}) as FrontendCompanyTestCategory;
				let availableBatchTypes = newCategory?.batchTypes;
				if (!userHasFullBatchAccess) {
					availableBatchTypes = availableBatchTypes.filter((batchType) =>
						user?.analystRoleGroup?.permittedBatchIds.includes(
							batchType.batchTypeId,
						),
					);
				}
				setSelectedBatchTypeId(
					availableBatchTypes.length > 0
						? availableBatchTypes[0].batchTypeId
						: "other",
				);
				setRequireDuplicateInstance(
					availableBatchTypes.length > 0
						? availableBatchTypes[0].requireDuplicateInstance
						: false,
				);
			}}
			displayEmpty
		/>
	);

	const selectBatchTypeDropdown: React.ReactNode = (
		<div style={{ width: "100%" }}>
			<CommonAutoCompleteDropdown
				id="select-batch-test-type-autocomplete"
				label={translation("SelectBatchType")}
				options={sortedBatchTestsOptions}
				groupBy={(batchType: FrontendBatchType) => {
					if (batchType.batchTypeId == "other") {
						return translation("Other");
					}
					const sameCategoryNode = selectedCategory?.hierarchyNodes.find(
						(node) => {
							return node.nodeId == batchType.testGroupNodeId;
						},
					);
					if (sameCategoryNode) {
						return sameCategoryNode.nodeName;
					} else {
						return translation("TestsFromOtherCategory");
					}
				}}
				value={selectedBatchType}
				setValue={(batchType: FrontendBatchType) => {
					setSelectedBatchTypeId(batchType.batchTypeId);
					setRequireDuplicateInstance(batchType.requireDuplicateInstance);
					setInstancesLoaded(false);
					//check if the assignee still have permission, for admins
					if (userCanAssignOtherAnalysts && analysts.length > 0) {
						// Keep only analysts who still have permission for the new batch type
						const permittedAnalystIds = analysts
							.filter((analyst) => {
								if (batchType.batchTypeId === "other") {
									return analyst.analystRoleGroup?.testCategoryOtherTestPermissions.includes(
										selectedCategoryId,
									);
								} else {
									return analyst.analystRoleGroup?.permittedBatchIds.includes(
										batchType.batchTypeId,
									);
								}
							})
							.map((analyst) => analyst.userId);

						// Filter out any assigned analysts who lost permission
						setAssignedAnalysts((prev) =>
							prev.filter((object) => {
								return permittedAnalystIds.includes(object.id);
							}),
						);
					}
				}}
				getOptionLabel={(batchType: FrontendBatchType) => {
					return batchType.batchName;
				}}
			/>
		</div>
	);

	if (!userCanAssignOtherAnalysts) {
		//analyst can only assign self
		analystOptions = [
			{
				title: `${user?.firstName} ${user?.lastName}`,
				id: "self",
			},
		];
	}

	const selectAssignedAnalystDropdown: React.ReactNode = (
		<CommonAutoCompleteDropdown
			id="select-assigned-analyst-dropdown"
			label={translation("AssignTo")}
			options={analystOptions}
			value={assignedAnalysts}
			setValue={(selected: Array<{ title: string; id: string }>) => {
				// Remove duplicates by id
				const unique = Array.from(
					new Map(selected.map((a) => [a.id, a])).values(),
				);
				setAssignedAnalysts(unique);
			}}
			disabled={!userCanAssignOtherAnalysts}
			multiple={true}
			getOptionLabel={(option) => option.title}
		/>
	);

	const requireDuplicateInstanceCheckbox: React.ReactNode =
		selectedBatchTypeId == "other" ? (
			<CustomCheckbox
				className={`require-duplicate-checkbox`}
				label={translation("RequireValidatingInstance")}
				labelClassName={"typography-body-02"}
				checked={requireDuplicateInstance}
				setChecked={() => {
					setRequireDuplicateInstance(!requireDuplicateInstance);
					if (requireDuplicateInstance) {
						//turn off, set to undefined
						setInstanceToVerify(undefined);
					} else {
						//set to first
						if (instancesToBatch.length > 0) {
							setInstanceToVerify(instancesToBatch[0]);
						}
					}
				}}
			/>
		) : null;

	const batchCodePrefixInput: React.ReactNode = (
		<CustomTextInput
			id={`batch-code-prefix-input`}
			value={batchCodePrefix}
			setValue={(value) => {
				setBatchCodePrefix(value);
			}}
			label={translation("BatchPrefix")}
			maxLength={10}
		/>
	);

	const populateBatchButton: React.ReactNode = (
		<GreenButton
			key="populate-batch-button"
			id="populate-batch-button"
			size="flex"
			children={
				loading ? (
					<ButtonTextLoadingAnimation
						text={translation("Loading")}
					></ButtonTextLoadingAnimation>
				) : (
					translation("PopulateBatch")
				)
			}
			disabled={loading}
			onClick={() => {
				handlePopulateBatch();
			}}
		></GreenButton>
	);

	const generationInfoSelection: React.ReactNode = (
		<div>
			{selectCategoryDropdown}
			{selectBatchTypeDropdown}
			{selectAssignedAnalystDropdown}
			{requireDuplicateInstanceCheckbox}
			{/* {batchCodePrefixInput} */}
			{/* {selectMicroTestMethodDropdown} */}
			{populateBatchButton}
		</div>
	);

	function updateSpecification(updatedSpec: FrontendSampleSpecification) {
		setAllInstances((prevInstances) => {
			// Map through the array to create a new array with the updated instance
			return prevInstances.map((instance) => {
				// Check if this specific instance contains the specification we just updated
				if (
					instance.specification?.specificationId ===
					updatedSpec.specificationId
				) {
					return {
						...instance,
						specification: updatedSpec, // Replace with the new data
					};
				}
				// Otherwise, return the instance as it was
				return instance;
			});
		});
	}

	const selectInstanceContent: React.ReactNode =
		allInstances.length == 0 ? (
			<div>{translation("NoInstance")}</div>
		) : (
			<div>
				{
					<BatchGenerationInstanceContainer
						company={company}
						setCompany={setCompany}
						user={user as FrontendUserInfo}
						testCategories={permittedCategories}
						selectedBatchType={selectedBatchType as FrontendBatchType}
						instancesToBatch={instancesToBatch}
						setInstancesToBatch={setInstancesToBatch}
						allInstances={allInstances}
						setUnsavedChanges={setUnsavedChanges}
						instanceToVerify={instanceToVerify}
						setInstanceToVerify={setInstanceToVerify}
						requireDuplicateInstance={requireDuplicateInstance}
						specDirectories={specDirectories}
						updateSpecification={updateSpecification}
					/>
				}
			</div>
		);

	const submitButton: React.ReactNode = (
		<GreenButton
			key="create-batch-button"
			id="create-batch-button"
			size="flex"
			children={
				loading ? (
					<ButtonTextLoadingAnimation
						text={translation("Loading")}
					></ButtonTextLoadingAnimation>
				) : (
					translation("CreateBatch")
				)
			}
			disabled={
				loading ||
				assignedAnalysts.length == 0 ||
				instancesToBatch.length == 0 ||
				(requireDuplicateInstance && !instanceToVerify)
			}
			onClick={() => {
				handleBatchGeneration();
			}}
		></GreenButton>
	);
	const selectInstanceSection: React.ReactNode = instancesLoaded ? (
		<div>
			{selectInstanceContent} {submitButton}
		</div>
	) : (
		<></>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			{generationInfoSelection}
			{selectInstanceSection}
		</div>
	);
}
